import { Combobox } from '@ibsys/kit';
import { IconButton, TruncatedText } from '@/shared/ui';
import { ExternalLinkIcon, XIcon } from 'lucide-react';
import { FC, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { KnowledgeSetListItem } from '@/entities/knowledge-set';
import { useSearchQuery } from '@/entities/search';
import { useHasPermission } from '@/entities/profile';
import type { BlockCardFormValues } from './types';

type RuleKnowledgeSetsProps = {
  index: number;
};

export const RuleKnowledgeSets: FC<RuleKnowledgeSetsProps> = ({ index }) => {
  const { t } = useTranslation('expertReport');
  const navigate = useNavigate();
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const { getValues, setValue, watch } = useFormContext<BlockCardFormValues>();

  const knowledgeSets = watch(`rules.${index}.knowledgeSets`);

  const { data: searchData } = useSearchQuery<KnowledgeSetListItem>({
    entityType: 'knowledge_set',
    filters: { isDeleted: false },
    limit: 200,
    offset: 0,
  });

  const linkedIds = useMemo(() => new Set(knowledgeSets.map((ks) => ks.id)), [knowledgeSets]);

  const comboboxOptions = useMemo(() => {
    const items = searchData?.items ?? [];
    return items.filter((item) => !linkedIds.has(item.id)).map((item) => ({ value: item.id, label: item.name }));
  }, [searchData, linkedIds]);

  const handleRemove = useCallback(
    (setId: number) => {
      const current = getValues(`rules.${index}.knowledgeSets`);
      setValue(
        `rules.${index}.knowledgeSets`,
        current.filter((ks) => ks.id !== setId),
        { shouldDirty: true },
      );
    },
    [index, getValues, setValue],
  );

  const handleAdd = useCallback(
    (value: unknown) => {
      const id = Number(value);
      const item = searchData?.items.find((i) => i.id === id);
      if (!item) return;

      const current = getValues(`rules.${index}.knowledgeSets`);
      setValue(`rules.${index}.knowledgeSets`, [...current, { id: item.id, name: item.name }], { shouldDirty: true });
    },
    [index, getValues, setValue, searchData],
  );

  const handleNavigate = useCallback(
    (setId: number) => {
      navigate(`/knowledge-sets/${setId}`);
    },
    [navigate],
  );

  const hasKnowledgeSets = knowledgeSets.length > 0;

  if (!hasKnowledgeSets) {
    // Без права на редактирование и без привязок показывать нечего
    if (!allowAdmin) return null;
    return (
      <div>
        <Combobox options={comboboxOptions} onChange={handleAdd} placeholder={t('linkKnowledgeSet')} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-muted-foreground">{t('knowledgeSets')}</div>

      <div className="flex flex-col gap-1">
        {knowledgeSets.map((ks) => (
          <div key={ks.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm">
            <TruncatedText className="flex-1">{ks.name}</TruncatedText>
            <IconButton
              variant="ghost"
              size="icon"
              onClick={() => handleNavigate(ks.id)}
              label={t('goToKnowledgeSet')}
              icon={<ExternalLinkIcon className="size-3.5" />}
            />
            {allowAdmin && (
              <IconButton
                variant="ghost"
                size="icon"
                onClick={() => handleRemove(ks.id)}
                label={t('removeKnowledgeSet')}
                icon={<XIcon className="size-3.5" />}
              />
            )}
          </div>
        ))}
      </div>

      {allowAdmin && comboboxOptions.length > 0 && (
        <Combobox options={comboboxOptions} onChange={handleAdd} placeholder={t('addKnowledgeSet')} />
      )}
    </div>
  );
};
